import * as React from 'react'

import { ProgressIndicatorStyle } from '../theme'

import { MAX_QUANTITY_SELECTION } from './BuildYourOwnBundle'
import {
  AddToBasketButton,
  Bar,
  BarWrapper,
  BundlePrice,
  BundlePriceText,
  BundlePriceWrapper,
  ButtonContainer,
  CircularCurrentIndicator,
  CircularProgressContainer,
  CircularWrapper,
  ProgressBarContainer,
  ProgressBarTotalContentText,
  StyledCircularProgress,
  StyledHiddenCircularProgress,
  StyledProgressBar,
  StyledTotalSummary,
} from './styles'

export enum ProgressIndicatorType {
  CIRCULAR = 'circular',
  BAR = 'bar',
}

export type ProgressBarPropsByob = {
  type: ProgressIndicatorType
  currentIndicator: number
  maxItems: number
  unlimitedProducts?: boolean
  totalPrice?: string
  bundleSummaryAmount?: string
  i18nText: {
    totalText?: string
    itemsText: string
    itemsAlternateText: string
    addToBagText: string
  }
  handleAddToBasketCallback: () => void
  style: ProgressIndicatorStyle
  disabledAddToBasket?: boolean
}

const shouldDisplayAddToBasket = (
  currentIndicator: number,
  maxItems: number,
  unlimitedProducts: boolean = false,
) =>
  currentIndicator === maxItems ||
  ((unlimitedProducts || !maxItems) && currentIndicator > 0)

const BarIndicator = (props: ProgressBarPropsByob) => {
  const maxProgressBarValue: number = 100
  const displayAddToBasket = shouldDisplayAddToBasket(
    props.currentIndicator,
    props.maxItems,
    props.unlimitedProducts,
  )

  return (
    <BarWrapper>
      <ProgressBarContainer>
        <ProgressBarTotalContentText>
          {`${props.currentIndicator} ${
            props.unlimitedProducts || !props.maxItems
              ? props.i18nText.itemsText
              : props.i18nText.itemsAlternateText
          }`}
        </ProgressBarTotalContentText>
        {!props.unlimitedProducts && props.maxItems && props.maxItems > 1 ? (
          <StyledProgressBar>
            <Bar
              completedRatio={
                (maxProgressBarValue / props.maxItems) * props.currentIndicator
              }
            />
          </StyledProgressBar>
        ) : (
          <BundlePrice>{props.totalPrice}</BundlePrice>
        )}
      </ProgressBarContainer>
      {displayAddToBasket && (
        <AddToBasketButton
          onClick={props.handleAddToBasketCallback}
          disabled={props.disabledAddToBasket}
        >
          {props.i18nText.addToBagText}
        </AddToBasketButton>
      )}
    </BarWrapper>
  )
}

const CircularIndicator = (props: ProgressBarPropsByob) => {
  const maxCircularProgress: number = 265
  const displayAddToBasket = shouldDisplayAddToBasket(
    props.currentIndicator,
    props.maxItems,
    props.unlimitedProducts,
  )

  return (
    <CircularWrapper>
      <CircularProgressContainer>
        <StyledCircularProgress
          progress={
            (maxCircularProgress / (props.maxItems || MAX_QUANTITY_SELECTION)) *
            props.currentIndicator
          }
        ></StyledCircularProgress>
        <StyledHiddenCircularProgress />
        <CircularCurrentIndicator textStyle={props.style.currentCountText.font}>
          {props.currentIndicator}
        </CircularCurrentIndicator>
        <span style={{ position: 'absolute', top: '150px' }}>
          {props.unlimitedProducts || !props.maxItems
            ? props.i18nText.itemsAlternateText
            : props.i18nText.itemsText}
        </span>
      </CircularProgressContainer>
      {displayAddToBasket && (
        <React.Fragment>
          <BundlePriceWrapper>
            <BundlePriceText>{props.i18nText.totalText}</BundlePriceText>
            {props.bundleSummaryAmount ? (
              <StyledTotalSummary content={props.bundleSummaryAmount} />
            ) : (
              <BundlePrice>{props.totalPrice}</BundlePrice>
            )}
          </BundlePriceWrapper>
          <ButtonContainer>
            <AddToBasketButton
              disabled={props.disabledAddToBasket}
              onClick={() => props.handleAddToBasketCallback()}
              isCircularBar={true}
            >
              {props.i18nText.addToBagText}
            </AddToBasketButton>
          </ButtonContainer>
        </React.Fragment>
      )}
    </CircularWrapper>
  )
}

export const ProgressIndicator = (props: ProgressBarPropsByob) => {
  switch (props.type) {
    case ProgressIndicatorType.CIRCULAR:
      return <CircularIndicator {...props} />

    case ProgressIndicatorType.BAR:
      return <BarIndicator {...props} />

    default:
      return null
  }
}
